import React, { useState, useEffect } from 'react';
import FileUploader from '../FileUploader';
import { MdAddAPhoto } from 'react-icons/md';
import { BsFiletypePdf } from 'react-icons/bs';
import PreviewModal from 'shared/media/PreviewModal';
// helpers
import { uniqueId } from 'lodash';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

// sub-components
const PhotoThumbnail = ({ src, fileType, ...props }) => (
  <span className='image-thumbnail'>
    {!fileType?.includes('pdf') && <img src={src} {...props} alt={fileType} />}
    {fileType?.includes('pdf') && (
      <div {...props} className='thumbnail-icon'><BsFiletypePdf size={40} /></div>
    )}
  </span>
);

const PhotoUploadIcon = ({ ...props }) => (
  <span
    className='upload-icon'
    {...props}
  >
    <MdAddAPhoto className='text-tertiary' size={40} />
  </span>
);

const defaultValue = [];
 
/**
 * An image file uploader displayed as a line of images with a preview and delete features.
 *
 * @typedef {Object} ImageFileUploaderProps
 * @property {number} [filesLimit={1}] The amount of images allowed to upload. 1 by default.
 * @property {number} [fileSizeMBLimit] The limit of a file size in MB allowed to upload.
 * @property {Array<string>} [allowedFileTypes] The list of file types allowed to upload.
 * @property {boolean} [allowPdf={true}] Indicates if PDF files are allowed. True by default.
 * @property {boolean} [disabled={false}] Indicates if the component is disabled. False by default.
 * @property {string} [name='photos'] The name of the field component. "Photos" by default.
 * @property {Array<{ name: string, urlForPreview: string, fileType: string, title: string }>} [value=[]] A list of uploaded images.
 * @property {Function} onChange A callback when new files are uploaded.
 * @property {string} [dataTestId] Data test Id.
 *
 * @param {ImageFileUploaderProps} props Component props.
 * @returns {React.ReactElement} Image file uploader.
 */
const ImageFileUploader = ({
  filesLimit = 1,
  fileSizeMBLimit,
  allowedFileTypes,
  allowPdf = true,
  disabled = false,
  name = 'photos',
  value = defaultValue,
  onChange,
  dataTestId,
}) => {
  const [photosState, setPhotosState] = useState(
    value?.map((photo) => ({ ...photo, uid: uniqueId() })) ?? [],
  );
  const [preview, setPreview] = useState(null);

  const photos = photosState;
  const setPhotos = setPhotosState;

  // calculated fields
  const remaining = filesLimit - photosState.length;

  // send updated photos to parent
  useEffect(() => {
    if (!onChange || disabled) {
      return;
    }
    onChange({ target: { name, value: photos } });
  }, [photos]);

  return (
    <div data-testid={dataTestId} className='image-file-uploader'>
      {photos.map((photo, ind) => (
        <PhotoThumbnail
          key={photo.uid ?? ind}
          src={photo.urlForPreview}
          fileType={photo.fileType}
          onClick={() => setPreview(photo)}
        />
      ))}
      {(disabled && photos.length === 0) && (
        <div className='empty-image-file-uploader'>No items</div>
      )}
      {remaining > 0 && !disabled && (
        <FileUploader
          filesLimit={filesLimit}
          fileSizeMBLimit={fileSizeMBLimit}
          allowedFileTypes={allowedFileTypes}
          imagesOnly
          allowPdf={allowPdf}
          onUpload={(files) => {
            const newFiles = files.map((file) => ({
              ...file,
              uid: uniqueId(),
              urlForPreview: `data:${file.fileType};base64,${file.data}`,
              date: (new Date()).toISOString(),
            }));
            setPhotos([...photos, ...newFiles]);
          }}
          Component={PhotoUploadIcon}
        />
      )}
      {preview && (
        <PreviewModal
          onClose={() => setPreview(null)}
          previewFile={preview}
          onDelete={() => {
            setPhotos(photos.filter((photo) => photo.uid !== preview.uid));
            setPreview(null);
          }}
          readOnly={disabled}
        />
      )}
    </div>
  );
};

export default ImageFileUploader;